/**
 * AdminScheduling Page
 * Main scheduling page with calendar and locations management
 * Refactored to use extracted components
 */
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import AdminLayout from "../components/AdminLayout";
import EditorialPageHeader from "../components/EditorialPageHeader";
import "./schedulingAthletic.css";
import { trackAdminWorkflowCompleted } from "../utils/adminPortalRollout";
import {
  Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight, Clock, MapPin,
  Loader2, Building2, Edit, Trash2, ChevronDown, ChevronUp,
  CalendarDays, CalendarRange, Video, Copy, Check, ExternalLink, Link2, Users, List
} from "lucide-react";
import { 
  format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, 
  addMonths, subMonths, startOfWeek, endOfWeek, addWeeks, subWeeks,
  addDays, subDays, isToday as isDateToday, parseISO, isSameMonth
} from "date-fns";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Card, CardContent } from "../components/ui/card";
import CreateMeetingDialog from "../components/CreateMeetingDialog";

// Import extracted scheduling components
import {
  EVENT_TYPES,
  SPORT_OPTIONS,
  getEventTypeStyle,
  DraggableEvent,
  DroppableDay,
  WeekHourSlot,
  DayHourSlot,
  SchedulingEventDialog,
  LocationDialog,
  SchedulingCommandCenter,
  SchedulingEventDrawer,
  needsSchedulingDetails
} from "../components/scheduling";

const EmbeddedLayout = ({ children }) => <>{children}</>;

const AdminScheduling = ({ embedded = false, facilitiesPath = "/admin/facilities" }) => {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [events, setEvents] = useState([]);
  const [serverConflicts, setServerConflicts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [teams, setTeams] = useState([]);
  const [locations, setLocations] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeEvent, setActiveEvent] = useState(null);
  
  // View mode: day, week, month
  const [viewMode, setViewMode] = useState("agenda");
  const [resourceView, setResourceView] = useState("date");
  
  // Filters
  const [selectedProgram, setSelectedProgram] = useState("all");
  const [selectedTeam, setSelectedTeam] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");
  
  // Tabs - initialize from URL query param if present
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    tabFromUrl && ["calendar", "meetings", "locations"].includes(tabFromUrl) ? tabFromUrl : "calendar"
  );
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === "calendar") next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next, { replace: true });
  };
  const [locationDialog, setLocationDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationForm, setLocationForm] = useState({ name: "", address: "", notes: "" });
  const [savingLocation, setSavingLocation] = useState(false);
  const [expandedLocations, setExpandedLocations] = useState({});
  
  // Google Calendar integration
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState({ connected: false });
  const [googleCalendarLoading, setGoogleCalendarLoading] = useState(false);
  
  // Meetings tab state
  const [meetings, setMeetings] = useState([]);
  const [meetingsLoading, setMeetingsLoading] = useState(false);
  const [meetingsError, setMeetingsError] = useState("");
  const [createMeetingDialogOpen, setCreateMeetingDialogOpen] = useState(false);
  const [copiedMeetingId, setCopiedMeetingId] = useState(null);
  const [deleteMeetingDialog, setDeleteMeetingDialog] = useState({ open: false, meeting: null });
  const [deletingMeeting, setDeletingMeeting] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState({ open: false, type: "", id: null, title: "", description: "" });
  const [confirmingAction, setConfirmingAction] = useState(false);
  
  const [eventForm, setEventForm] = useState({
    title: "",
    type: "practice",
    program_id: "",
    team_id: "",
    date: "",
    start_time: "",
    end_time: "",
    location: "",
    meet_link: "",
    notes: "",
    is_recurring: false,
    recurrence_pattern: "",
    recurrence_end: "",
    additional_dates: [],
    send_notification: false
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    fetchData();
    fetchGoogleCalendarStatus();
    
    // Check URL for Google Calendar connection result
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'google_calendar_connected') {
      toast.success("Google Calendar connected successfully!");
      setGoogleCalendarStatus({ connected: true });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (urlParams.get('error') === 'google_auth_failed') {
      toast.error("Failed to connect Google Calendar. Please try again.");
      window.history.replaceState({}, '', window.location.pathname);
    } else if (urlParams.get('google_calendar') === 'connected') {
      toast.success("Google Calendar connected!");
      setGoogleCalendarStatus({ connected: true });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [token, currentDate]);

  const fetchGoogleCalendarStatus = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/google/calendar/status`, { headers });
      setGoogleCalendarStatus(response.data);
    } catch (error) {
      console.log("Google Calendar status check failed:", error.message);
    }
  };

  const connectGoogleCalendar = async () => {
    setGoogleCalendarLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/google/calendar/connect`, { headers });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error("Failed to start Google Calendar connection");
      setGoogleCalendarLoading(false);
    }
  };

  const performDisconnectGoogleCalendar = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/google/calendar/disconnect`, {}, { headers });
      setGoogleCalendarStatus({ connected: false });
      setMeetings([]);
      toast.success("Google Calendar disconnected");
    } catch (error) {
      toast.error("Failed to disconnect Google Calendar");
    }
  };

  // Fetch meetings for the Meetings tab
  const fetchMeetings = async () => {
    try {
      setMeetingsLoading(true);
      setMeetingsError("");
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/google/meetings`, { headers });
      setMeetings(response.data.meetings || []);
    } catch (error) {
      console.error("Error fetching meetings:", error);
      setMeetingsError("Meetings could not be loaded. Your Google Calendar connection was not changed.");
    } finally {
      setMeetingsLoading(false);
    }
  };

  // Fetch meetings when tab changes to meetings or when calendar status changes
  useEffect(() => {
    if (activeTab === "meetings" && googleCalendarStatus.connected) {
      fetchMeetings();
    }
  }, [activeTab, googleCalendarStatus.connected]);

  const copyMeetLink = (meeting) => {
    navigator.clipboard.writeText(meeting.meet_link);
    setCopiedMeetingId(meeting.meeting_id);
    toast.success("Meet link copied!");
    setTimeout(() => setCopiedMeetingId(null), 2000);
  };

  const handleDeleteMeeting = async () => {
    if (!deleteMeetingDialog.meeting) return;
    
    setDeletingMeeting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/google/calendar/events/${deleteMeetingDialog.meeting.google_event_id}`, { headers });
      toast.success("Meeting deleted");
      fetchMeetings();
    } catch (error) {
      toast.error("Failed to delete meeting");
    } finally {
      setDeletingMeeting(false);
      setDeleteMeetingDialog({ open: false, meeting: null });
    }
  };

  const formatMeetingDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { 
      weekday: "short", 
      month: "short", 
      day: "numeric",
      year: "numeric"
    });
  };

  const formatMeetingTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", { 
      hour: "numeric", 
      minute: "2-digit",
      hour12: true
    });
  };

  const isMeetingPast = (dateString) => {
    return new Date(dateString) < new Date();
  };

  const upcomingMeetings = meetings.filter(m => !isMeetingPast(m.end_time));
  const pastMeetings = meetings.filter(m => isMeetingPast(m.end_time));

  const fetchData = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const startDate = format(startOfMonth(subMonths(currentDate, 1)), "yyyy-MM-dd");
      const endDate = format(endOfMonth(addMonths(currentDate, 2)), "yyyy-MM-dd");
      const [eventsRes, conflictsRes, programsRes, teamsRes, locationsRes] = await Promise.all([
        axios.get(`${API}/admin/events`, { headers, params: { start_date: startDate, end_date: endDate } }),
        axios.get(`${API}/admin/events/conflicts`, { headers, params: { start_date: startDate, end_date: endDate } }).catch(() => ({ data: { conflicts: [] } })),
        axios.get(`${API}/programs`, { headers }),
        axios.get(`${API}/admin/teams`, { headers }),
        axios.get(`${API}/facilities/locations`, { headers }).catch(() => ({ data: [] }))
      ]);
      setEvents(eventsRes.data);
      setServerConflicts(conflictsRes.data.conflicts || []);
      // Handle paginated response - programs API returns {items: [...]}
      const programsData = programsRes.data?.items || programsRes.data || [];
      setPrograms(Array.isArray(programsData) ? programsData : []);
      // Handle paginated response - teams API might return {items: [...]}
      const teamsData = teamsRes.data?.items || teamsRes.data || [];
      setTeams(Array.isArray(teamsData) ? teamsData : []);
      
      // Fetch facilities for each location
      const locsData = locationsRes.data?.items || locationsRes.data || [];
      const locationsArray = Array.isArray(locsData) ? locsData : [];
      const locsWithFacilities = await Promise.all(
        locationsArray.map(async (loc) => {
          try {
            const facRes = await axios.get(`${API}/facilities/locations/${loc.location_id}`, { headers });
            return { ...loc, facilities: facRes.data.facilities || [] };
          } catch {
            return { ...loc, facilities: [] };
          }
        })
      );
      setLocations(locsWithFacilities);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoadError("The schedule could not be loaded. No events or notifications were changed.");
    } finally {
      setLoading(false);
    }
  };

  // Get teams for selected program
  const filteredTeams = useMemo(() => {
    if (selectedProgram === "all") return teams;
    return teams.filter(t => t.program_id === selectedProgram);
  }, [teams, selectedProgram]);

  // Filter events based on selected program and team
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      if (selectedProgram !== "all" && event.program_id !== selectedProgram) return false;
      if (selectedTeam !== "all" && event.team_id !== selectedTeam && !event.team_ids?.includes(selectedTeam)) return false;
      if (selectedType !== "all" && event.type !== selectedType) return false;
      if (selectedLocation === "missing" && event.location) return false;
      if (selectedLocation !== "all" && selectedLocation !== "missing" && event.location !== selectedLocation) return false;
      return true;
    });
  }, [events, selectedLocation, selectedProgram, selectedTeam, selectedType]);

  const schedulingConflicts = serverConflicts;
  const needsAttention = useMemo(() => events.filter(needsSchedulingDetails), [events]);
  const selectedEventConflicts = useMemo(() => selectedEvent
    ? schedulingConflicts.filter((conflict) => conflict.first.event_id === selectedEvent.event_id || conflict.second.event_id === selectedEvent.event_id)
    : [], [schedulingConflicts, selectedEvent]);

  // Reset team filter when program changes
  useEffect(() => {
    setSelectedTeam("all");
  }, [selectedProgram]);

  // Build location options for event form
  const locationOptions = useMemo(() => {
    const options = [];
    
    // Add virtual meeting options first
    options.push({ 
      value: "google_meet", 
      label: "🎥 Google Meet (Virtual)", 
      type: "virtual",
      icon: "video"
    });
    
    // Add physical locations
    locations.forEach(loc => {
      options.push({ value: loc.name, label: loc.name, type: "location" });
      loc.facilities?.forEach(fac => {
        options.push({ 
          value: `${loc.name} - ${fac.name}`, 
          label: `${loc.name} - ${fac.name}`,
          type: "facility"
        });
      });
    });
    return options;
  }, [locations]);

  // Calendar navigation
  const navigatePrev = () => {
    if (viewMode === "month" || viewMode === "agenda") setCurrentDate(subMonths(currentDate, 1));
    else if (viewMode === "week") setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(subDays(currentDate, 1));
  };

  const navigateNext = () => {
    if (viewMode === "month" || viewMode === "agenda") setCurrentDate(addMonths(currentDate, 1));
    else if (viewMode === "week") setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addDays(currentDate, 1));
  };

  const navigateToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const getCalendarTitle = () => {
    if (viewMode === "month" || viewMode === "agenda") return format(currentDate, "MMMM yyyy");
    if (viewMode === "week") {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      return `${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d, yyyy")}`;
    }
    return format(currentDate, "EEEE, MMMM d, yyyy");
  };

  const getEventsForDay = (date) => {
    if (!date) return [];
    return filteredEvents.filter(event => 
      event.date && isSameDay(parseISO(event.date), date)
    );
  };

  // Event dialog handlers
  const openNewEvent = (date = null, hour = null) => {
    setEditingEvent(null);
    setEventForm({
      title: "",
      type: "practice",
      program_id: selectedProgram !== "all" ? selectedProgram : "",
      team_id: selectedTeam !== "all" ? selectedTeam : "",
      date: date ? format(date, "yyyy-MM-dd") : "",
      start_time: hour !== null ? `${hour.toString().padStart(2, '0')}:00` : "",
      end_time: hour !== null ? `${(hour + 1).toString().padStart(2, '0')}:00` : "",
      location: "",
      notes: "",
      is_recurring: false,
      recurrence_pattern: "",
      recurrence_end: "",
      additional_dates: [],
      send_notification: false
    });
    setEventDialogOpen(true);
  };

  const openEditEvent = (event) => {
    if (event.source_type === "LEAGUE_FIXTURE") {
      setSelectedEvent(event);
      toast.info("Published League fixtures are managed from the League schedule");
      return;
    }
    setEditingEvent(event);
    setEventForm({
      title: event.title,
      type: event.type,
      program_id: event.program_id || "",
      team_id: event.team_id || "",
      date: event.date || "",
      start_time: event.start_time || "",
      end_time: event.end_time || "",
      location: event.location || "",
      meet_link: event.meet_link || "",
      notes: event.notes || "",
      send_notification: false
    });
    setEventDialogOpen(true);
  };

  const openEventDetails = (event) => setSelectedEvent(event);

  const handleSaveEvent = async () => {
    if (!eventForm.title || !eventForm.date) {
      toast.error("Please fill in title and date");
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let googleCalendarEventId = null;
      let realMeetLink = null;
      
      // If Google Calendar is connected and this is a Google Meet event, create in Google Calendar first
      if (googleCalendarStatus.connected && eventForm.location === "google_meet" && !editingEvent) {
        try {
          const startDateTime = `${eventForm.date}T${eventForm.start_time || '09:00'}:00`;
          const endDateTime = `${eventForm.date}T${eventForm.end_time || '10:00'}:00`;
          
          const gcalResponse = await axios.post(`${API}/google/calendar/events`, {
            title: eventForm.title,
            description: eventForm.notes || "",
            start_time: startDateTime,
            end_time: endDateTime,
            add_meet_link: true,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles"
          }, { headers });
          
          googleCalendarEventId = gcalResponse.data.event_id;
          realMeetLink = gcalResponse.data.meet_link;
          
          toast.success(
            <div>
              <p>Event created with Google Meet!</p>
              {realMeetLink && (
                <p className="text-xs mt-1 text-blue-600">
                  <a href={realMeetLink} target="_blank" rel="noopener noreferrer" className="underline">
                    {realMeetLink}
                  </a>
                </p>
              )}
            </div>
          );
        } catch (gcalError) {
          console.error("Google Calendar error:", gcalError);
          toast.warning("Google Calendar sync failed. Event will be created locally with a generated meet link.");
        }
      }
      
      // Create/update event in our database
      const eventData = {
        ...eventForm,
        meet_link: realMeetLink || eventForm.meet_link,
        google_calendar_event_id: googleCalendarEventId
      };
      
      if (editingEvent) {
        const response = await axios.put(`${API}/admin/events/${editingEvent.event_id}`, eventData, { headers });
        if (!realMeetLink && response.data.meet_link && eventForm.location === "google_meet") {
          toast.success(
            <div>
              <p>Event updated!</p>
              <p className="text-xs mt-1 text-blue-600">Meet link: {response.data.meet_link}</p>
            </div>
          );
        } else if (!realMeetLink) {
          toast.success("Event updated");
        }
      } else {
        const response = await axios.post(`${API}/admin/events`, eventData, { headers });
        if (!realMeetLink && response.data.meet_link && eventForm.location === "google_meet") {
          toast.success(
            <div>
              <p>{eventForm.is_recurring ? "Recurring events created!" : "Event created!"}</p>
              <p className="text-xs mt-1 text-blue-600">Meet link: {response.data.meet_link}</p>
            </div>
          );
        } else if (!realMeetLink) {
          const hasMultipleDates = eventForm.additional_dates?.filter(d => d).length > 0;
          const msg = eventForm.is_recurring ? "Recurring events created" : hasMultipleDates ? response.data.message || "Events created" : "Event created";
          toast.success(msg);
        }
      }
      setEventDialogOpen(false);
      trackAdminWorkflowCompleted("scheduling");
      fetchData();
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || "Failed to save event");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    setConfirmationDialog({ open: true, type: "event", id: eventId, title: "Remove event?", description: "This removes the event from the shared schedule. Families and coaches are not notified automatically; send a schedule-change message first if they need an alert." });
  };

  const performDeleteEvent = async (eventId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/events/${eventId}`, { headers });
      toast.success("Event removed from the shared schedule");
      fetchData();
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Failed to delete event");
    }
  };

  // Drag and drop
  const handleDragStart = (event) => {
    const draggedEvent = events.find(e => e.event_id === event.active.id);
    if (draggedEvent?.source_type === "LEAGUE_FIXTURE") {
      toast.info("Published League fixtures are managed from the League schedule");
      return;
    }
    setActiveEvent(draggedEvent);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveEvent(null);
    
    if (!over || !over.data.current) return;
    
    const draggedEvent = events.find(e => e.event_id === active.id);
    if (!draggedEvent) return;
    if (draggedEvent.source_type === "LEAGUE_FIXTURE") return;
    
    const overData = over.data.current;
    let newDate, newStartTime, newEndTime;
    
    if (overData.date && !overData.hour && overData.hour !== 0) {
      newDate = format(overData.date, "yyyy-MM-dd");
      newStartTime = draggedEvent.start_time;
      newEndTime = draggedEvent.end_time;
    } else if (overData.date && (overData.hour || overData.hour === 0)) {
      newDate = format(overData.date, "yyyy-MM-dd");
      newStartTime = `${overData.hour.toString().padStart(2, '0')}:00`;
      
      if (draggedEvent.start_time && draggedEvent.end_time) {
        const oldStart = parseInt(draggedEvent.start_time.split(':')[0]);
        const oldEnd = parseInt(draggedEvent.end_time.split(':')[0]);
        const duration = oldEnd - oldStart;
        newEndTime = `${(overData.hour + duration).toString().padStart(2, '0')}:00`;
      } else {
        newEndTime = `${(overData.hour + 1).toString().padStart(2, '0')}:00`;
      }
    } else {
      return;
    }
    
    const oldDate = draggedEvent.date;
    if (newDate === oldDate && newStartTime === draggedEvent.start_time) return;
    
    // Optimistic update
    setEvents(prev => prev.map(e => 
      e.event_id === draggedEvent.event_id 
        ? { ...e, date: newDate, start_time: newStartTime, end_time: newEndTime }
        : e
    ));
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/events/${draggedEvent.event_id}`, {
        ...draggedEvent,
        date: newDate,
        start_time: newStartTime,
        end_time: newEndTime
      }, { headers });
      toast.success(`Moved "${draggedEvent.title}"`);
    } catch (error) {
      console.error("Error moving event:", error);
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || "Failed to move event");
      setEvents(prev => prev.map(e => 
        e.event_id === draggedEvent.event_id 
          ? { ...e, date: oldDate, start_time: draggedEvent.start_time, end_time: draggedEvent.end_time }
          : e
      ));
    }
  };

  // Location handlers
  const handleSaveLocation = async () => {
    if (!locationForm.name) {
      toast.error("Please enter a location name");
      return;
    }
    setSavingLocation(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingLocation) {
        await axios.put(`${API}/facilities/locations/${editingLocation.location_id}`, locationForm, { headers });
        toast.success("Location updated");
      } else {
        await axios.post(`${API}/facilities/locations`, locationForm, { headers });
        toast.success("Location created");
      }
      setLocationDialog(false);
      setEditingLocation(null);
      setLocationForm({ name: "", address: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to save location");
    } finally {
      setSavingLocation(false);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    const location = locations.find((item) => item.location_id === locationId);
    setConfirmationDialog({ open: true, type: "location", id: locationId, title: "Delete location?", description: `Delete ${location?.name || "this location"} and all of its fields and courts? This cannot be undone.` });
  };

  const performDeleteLocation = async (locationId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/facilities/locations/${locationId}`, { headers });
      toast.success("Location deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete location");
    }
  };

  const handleConfirmAction = async () => {
    setConfirmingAction(true);
    try {
      if (confirmationDialog.type === "disconnect") await performDisconnectGoogleCalendar();
      if (confirmationDialog.type === "event") await performDeleteEvent(confirmationDialog.id);
      if (confirmationDialog.type === "location") await performDeleteLocation(confirmationDialog.id);
      setConfirmationDialog({ open: false, type: "", id: null, title: "", description: "" });
    } finally {
      setConfirmingAction(false);
    }
  };

  // Render month view
  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    return (
      <div className="overflow-x-auto rounded-3xl border border-[#dfe6df] bg-white" aria-label="Month calendar" tabIndex="0">
        <div className="min-w-[720px]">
        <div className="grid grid-cols-7 border-b border-slate-100">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
            <div key={day} className="p-3 text-center text-sm font-medium text-slate-500 bg-slate-50">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day, idx) => {
            const dayEvents = getEventsForDay(day);
            const isToday = isDateToday(day);
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const isCurrentMonth = isSameMonth(day, currentDate);
            
            return (
              <DroppableDay
                key={idx}
                day={day}
                isToday={isToday}
                isSelected={isSelected}
                isCurrentMonth={isCurrentMonth}
                onDayClick={setSelectedDate}
                onDoubleClick={openNewEvent}
              >
                {dayEvents.slice(0, 3).map(event => (
                  <DraggableEvent
                    key={event.event_id}
                    event={event}
                    onClick={openEventDetails}
                    compact
                  />
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-xs text-slate-500 pl-1">
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </DroppableDay>
            );
          })}
        </div>
        </div>
      </div>
    );
  };

  // Render week view
  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const hours = Array.from({ length: 14 }, (_, i) => i + 6); // 6 AM to 7 PM

    return (
      <div className="overflow-x-auto rounded-3xl border border-[#dfe6df] bg-white" aria-label="Week calendar" tabIndex="0">
        <div className="min-w-[880px]">
        <div className="grid grid-cols-8 border-b border-slate-100">
          <div className="p-2 text-center text-xs font-medium text-slate-500 bg-slate-50 border-r border-slate-100">
            Time
          </div>
          {weekDays.map(day => (
            <div
              key={day.toISOString()}
              className={`p-2 text-center border-r border-slate-100 bg-slate-50 ${isDateToday(day) ? 'bg-blue-50' : ''}`}
            >
              <div className="text-xs font-medium text-slate-500">{format(day, "EEE")}</div>
              <div className={`text-sm font-semibold ${isDateToday(day) ? 'text-blue-600' : 'text-slate-900'}`}>
                {format(day, "d")}
              </div>
            </div>
          ))}
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          {hours.map(hour => (
            <div key={hour} className="grid grid-cols-8">
              <div className="p-2 text-xs text-slate-500 border-r border-b border-slate-100 bg-slate-50/50">
                {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
              </div>
              {weekDays.map(day => {
                const dayEvents = getEventsForDay(day);
                return (
                  <WeekHourSlot
                    key={`${day.toISOString()}-${hour}`}
                    date={day}
                    hour={hour}
                    events={dayEvents}
                    onEventClick={openEventDetails}
                    onSlotClick={(d, h) => openNewEvent(d, h)}
                  />
                );
              })}
            </div>
          ))}
        </div>
        </div>
      </div>
    );
  };

  // Render day view
  const renderDayView = () => {
    const hours = Array.from({ length: 14 }, (_, i) => i + 6); // 6 AM to 7 PM
    const dayEvents = getEventsForDay(currentDate);

    return (
      <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-slate-100 bg-slate-50">
          <h3 className="text-lg font-semibold text-slate-900">{format(currentDate, "EEEE, MMMM d, yyyy")}</h3>
          <p className="text-sm text-slate-500">{dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="grid grid-cols-[80px,1fr] max-h-[500px] overflow-y-auto">
          {hours.map(hour => (
            <div key={hour} className="contents">
              <div className="p-3 text-sm text-slate-500 border-r border-b border-slate-100 bg-slate-50/50">
                {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
              </div>
              <DayHourSlot
                date={currentDate}
                hour={hour}
                events={dayEvents}
                onEventClick={openEventDetails}
                onSlotClick={(d, h) => openNewEvent(d, h)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAgendaView = () => {
    const monthStart = format(startOfMonth(currentDate), "yyyy-MM-dd");
    const monthEnd = format(endOfMonth(currentDate), "yyyy-MM-dd");
    const agendaEvents = filteredEvents
      .filter((event) => event.date >= monthStart && event.date <= monthEnd)
      .sort((first, second) => `${first.date} ${first.start_time || ""}`.localeCompare(`${second.date} ${second.start_time || ""}`));
    const groups = agendaEvents.reduce((result, event) => {
      let key = event.date || "Unscheduled";
      if (resourceView === "team") {
        key = teams.find((team) => team.team_id === event.team_id)?.name || "No team assigned";
      } else if (resourceView === "location") {
        key = event.location === "google_meet" ? "Google Meet" : (event.location || "No location assigned");
      }
      result[key] = [...(result[key] || []), event];
      return result;
    }, {});

    if (!agendaEvents.length) {
      return <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center"><CalendarDays className="mx-auto h-10 w-10 text-slate-300" /><h3 className="mt-3 font-semibold text-slate-900">No events in {format(currentDate, "MMMM")}</h3><p className="mt-1 text-sm text-slate-500">Use Create event, adjust the filters, or move to another month.</p></div>;
    }

    return (
      <div className="space-y-4" data-testid="scheduling-agenda">
        {Object.entries(groups).map(([group, groupEvents]) => (
          <section key={group} className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 bg-[#f7f7f2] px-5 py-3">
              <h3 className="font-semibold text-slate-950">{resourceView === "date" && group !== "Unscheduled" ? format(parseISO(group), "EEEE, MMMM d") : group}</h3>
              <p className="text-xs text-slate-500">{groupEvents.length} event{groupEvents.length === 1 ? "" : "s"}</p>
            </div>
            <div className="divide-y divide-slate-100">
              {groupEvents.map((event) => {
                const team = teams.find((item) => item.team_id === event.team_id);
                const program = programs.find((item) => item.program_id === event.program_id);
                return <button key={event.event_id} type="button" onClick={() => openEventDetails(event)} className="schedule-agenda-row"><div>{resourceView !== "date" && <time className="block text-xs text-slate-600" dateTime={event.date}>{event.date}</time>}<p className="text-sm font-semibold text-slate-950">{event.start_time || "Time needed"}</p><p className="text-xs text-slate-500">{event.end_time ? `to ${event.end_time}` : "No end time"}</p></div><div><p className="font-semibold text-slate-900">{event.title}</p><p className="mt-1 text-sm text-slate-500">{program?.name || "No program"}{team ? ` · ${team.name}` : ""}</p></div><div className="text-sm text-slate-600 sm:text-right"><p>{event.location === "google_meet" ? "Google Meet" : (event.location || "Location needed")}</p><p className="mt-1 text-xs font-semibold uppercase tracking-wide text-teal-700">{event.type || "event"}</p></div></button>;
              })}
            </div>
          </section>
        ))}
      </div>
    );
  };

  // Render locations tab
  const renderLocationsTab = () => {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Locations & Fields</h2>
            <p className="text-sm text-slate-500">Review scheduling locations and their available fields or courts.</p>
          </div>
          <Button
            onClick={() => navigate(facilitiesPath)}
            className="self-start rounded-full bg-[#102a27] hover:bg-[#173d38] sm:self-auto"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Manage Facilities
          </Button>
        </div>

        {locations.length === 0 ? (
          <div className="bg-white border border-slate-200/60 rounded-2xl p-12 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No locations yet</h3>
            <p className="text-slate-500 mb-4">Add venues, fields, and courts in the Facilities workspace.</p>
            <Button onClick={() => navigate(facilitiesPath)} className="bg-slate-900 rounded-xl">
              <ExternalLink className="w-4 h-4 mr-2" /> Manage Facilities
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {locations.map(loc => (
              <div key={loc.location_id} className="overflow-hidden rounded-3xl border border-[#dfe6df] bg-white">
                <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#087f67]" onClick={() => setExpandedLocations(prev => ({ ...prev, [loc.location_id]: !prev[loc.location_id] }))} aria-expanded={Boolean(expandedLocations[loc.location_id])} aria-controls={`location-${loc.location_id}-facilities`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{loc.name}</h3>
                      {loc.address && <p className="text-sm text-slate-500">{loc.address}</p>}
                    </div>
                  </div>
                    {expandedLocations[loc.location_id] ? <ChevronUp className="ml-auto h-5 w-5 flex-shrink-0 text-slate-400" /> : <ChevronDown className="ml-auto h-5 w-5 flex-shrink-0 text-slate-400" />}
                  </button>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-slate-500">{loc.facilities?.length || 0} fields/courts</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setEditingLocation(loc); setLocationForm({ name: loc.name, address: loc.address || "", notes: loc.notes || "" }); setLocationDialog(true); }}
                      className="rounded-lg"
                      aria-label={`Edit ${loc.name}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleDeleteLocation(loc.location_id); }}
                      className="rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
                      aria-label={`Delete ${loc.name}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {expandedLocations[loc.location_id] && (
                  <div id={`location-${loc.location_id}-facilities`} className="border-t border-slate-100 bg-[#f5f7f4] p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-slate-700">Fields & Courts</h4>
                      <Button variant="outline" size="sm" onClick={() => navigate(facilitiesPath)} className="rounded-lg">
                        <ExternalLink className="w-4 h-4 mr-1" /> Manage
                      </Button>
                    </div>
                    {!loc.facilities || loc.facilities.length === 0 ? (
                      <p className="text-sm text-slate-500">No fields or courts added yet</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {loc.facilities.map(fac => (
                          <div key={fac.facility_id} className="bg-white rounded-xl p-3 border border-slate-200 flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{fac.name}</p>
                              <p className="text-xs text-slate-500">
                                {SPORT_OPTIONS.find(s => s.value === fac.sport)?.label || fac.sport}
                                {fac.size && ` • ${fac.size}`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <PageLayout title="Scheduling" subtitle="Plan events, resolve conflicts, and keep every team in the right place." headerEyebrow="Operations">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </PageLayout>
    );
  }

  if (loadError) {
    return (
      <PageLayout title="Scheduling" subtitle="Plan events, resolve conflicts, and keep every team in the right place." headerEyebrow="Operations">
        <div className="mx-auto max-w-xl rounded-2xl border border-rose-200 bg-rose-50 p-6" role="alert">
          <h2 className="text-lg font-semibold text-rose-950">Scheduling unavailable</h2>
          <p className="mt-2 text-sm text-rose-800">{loadError}</p>
          <Button onClick={fetchData} className="mt-4 rounded-xl bg-rose-700 hover:bg-rose-800">Try again</Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="space-y-6" data-testid="admin-scheduling" data-schedule-main={activeTab === "calendar"}>
        <EditorialPageHeader
          eyebrow={activeTab === "calendar" ? undefined : "Operations"}
          title="Scheduling"
          description="Plan events, resolve conflicts, and keep every team in the right place."
          headingId="scheduling-heading"
          aside={<Button onClick={() => openNewEvent()} className="schedule-create w-full rounded-full bg-[#b7f34a] text-[#102a27] hover:bg-[#c9ff63] sm:w-auto"><Plus className="mr-2 h-4 w-4" />{activeTab === "calendar" ? "Create event" : "Add event"}</Button>}
        />
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid h-auto w-full grid-cols-3 rounded-2xl bg-slate-100 p-1 sm:inline-flex sm:w-auto">
            <TabsTrigger value="calendar" className="rounded-lg data-[state=active]:bg-white" data-testid="scheduling-calendar-tab">
              <CalendarIcon className="w-4 h-4 mr-2" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="meetings" className="rounded-lg data-[state=active]:bg-white" data-testid="scheduling-meetings-tab">
              <Video className="w-4 h-4 mr-2" />
              Meetings
            </TabsTrigger>
            <TabsTrigger value="locations" className="rounded-lg data-[state=active]:bg-white" data-testid="scheduling-locations-tab">
              <Building2 className="w-4 h-4 mr-2" />
              Locations
            </TabsTrigger>
          </TabsList>

          <TabsContent value="calendar" className="schedule-calendar mt-6">
            <SchedulingCommandCenter
              conflicts={schedulingConflicts}
              needsAttention={needsAttention}
              selectedType={selectedType}
              setSelectedType={setSelectedType}
              selectedLocation={selectedLocation}
              setSelectedLocation={setSelectedLocation}
              locations={locations}
              events={filteredEvents}
              resourceView={resourceView}
              setResourceView={setResourceView}
              programs={programs}
              selectedProgram={selectedProgram}
              setSelectedProgram={setSelectedProgram}
              filteredTeams={filteredTeams}
              selectedTeam={selectedTeam}
              setSelectedTeam={setSelectedTeam}
              onSelectEvent={openEventDetails}
            />
            <div className="space-y-4">
                {/* Calendar Header */}
                <div className="schedule-calendar-toolbar">
                  <div className="schedule-date-controls">
                    <Button variant="outline" size="sm" onClick={navigateToday} className="rounded-xl border-slate-200">
                      Today
                    </Button>
                    <Button variant="outline" size="icon" onClick={navigatePrev} className="rounded-xl border-slate-200" aria-label={`Previous ${viewMode === "week" ? "week" : viewMode === "day" ? "day" : "month"}`}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={navigateNext} className="rounded-xl border-slate-200" aria-label={`Next ${viewMode === "week" ? "week" : viewMode === "day" ? "day" : "month"}`}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <h2 className="text-xl font-semibold text-slate-900 ml-2">
                      {getCalendarTitle()}
                    </h2>
                  </div>

                  {/* View Mode Toggle */}
                  <div className="schedule-view-controls" role="group" aria-label="Calendar view">
                    <Button
                      variant={viewMode === "agenda" ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setViewMode("agenda")}
                      aria-pressed={viewMode === "agenda"}
                      className={`rounded-lg ${viewMode === "agenda" ? "bg-white shadow-sm" : ""}`}
                    >
                      <List className="w-4 h-4 mr-1" />
                      Agenda
                    </Button>
                    <Button
                      variant={viewMode === "day" ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setViewMode("day")}
                      aria-pressed={viewMode === "day"}
                      className={`rounded-lg ${viewMode === "day" ? "bg-white shadow-sm" : ""}`}
                    >
                      <CalendarIcon className="w-4 h-4 mr-1" />
                      Day
                    </Button>
                    <Button
                      variant={viewMode === "week" ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setViewMode("week")}
                      aria-pressed={viewMode === "week"}
                      className={`rounded-lg ${viewMode === "week" ? "bg-white shadow-sm" : ""}`}
                    >
                      <CalendarDays className="w-4 h-4 mr-1" />
                      Week
                    </Button>
                    <Button
                      variant={viewMode === "month" ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setViewMode("month")}
                      aria-pressed={viewMode === "month"}
                      className={`rounded-lg ${viewMode === "month" ? "bg-white shadow-sm" : ""}`}
                    >
                      <CalendarRange className="w-4 h-4 mr-1" />
                      Month
                    </Button>
                  </div>
                </div>

                {/* Calendar Grid with DnD */}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  {viewMode === "agenda" && renderAgendaView()}
                  {viewMode === "month" && renderMonthView()}
                  {viewMode === "week" && renderWeekView()}
                  {viewMode === "day" && renderDayView()}

                  <DragOverlay>
                    {activeEvent ? (
                      <div className={`text-xs px-3 py-2 rounded-lg shadow-xl ${getEventTypeStyle(activeEvent.type)}`}>
                        <div className="font-medium">{activeEvent.title}</div>
                        {activeEvent.start_time && (
                          <div className="text-xs opacity-75">{activeEvent.start_time}</div>
                        )}
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>

                {/* Selected Day Events (Month View) */}
                {viewMode === "month" && selectedDate && (
                  <div className="bg-white border border-slate-200/60 rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-slate-900">
                        {format(selectedDate, "EEEE, MMMM d")}
                      </h3>
                      <Button size="sm" variant="outline" onClick={() => openNewEvent(selectedDate)} className="rounded-xl">
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                    {getEventsForDay(selectedDate).length === 0 ? (
                      <p className="text-sm text-slate-500">No events scheduled</p>
                    ) : (
                      <div className="space-y-2">
                        {getEventsForDay(selectedDate).map(event => (
                          <div
                            key={event.event_id}
                            className={`group rounded-xl border p-3 transition-shadow hover:shadow-sm ${getEventTypeStyle(event.type)}`}
                            onClick={() => openEditEvent(event)}
                            onKeyDown={(keyboardEvent) => {
                              if (keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
                                keyboardEvent.preventDefault();
                                openEditEvent(event);
                              }
                            }}
                            role="button"
                            tabIndex="0"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium">{event.title}</div>
                                {event.start_time && (
                                  <div className="text-xs opacity-75 flex items-center gap-1 mt-1">
                                    <Clock className="w-3 h-3" />
                                    {event.start_time}{event.end_time && ` - ${event.end_time}`}
                                  </div>
                                )}
                                {event.location && (
                                  <div className="text-xs opacity-75 flex items-center gap-1 mt-1">
                                    <MapPin className="w-3 h-3" />
                                    {event.location}
                                  </div>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="rounded-lg opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
                                onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event.event_id); }}
                                aria-label={`Delete ${event.title}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {/* Google Calendar Connection Banner */}
                {!googleCalendarStatus.connected ? (
                  <div className="flex flex-col gap-3 rounded-2xl border border-[#dfe6df] bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">Connect Google Calendar</p>
                        <p className="text-xs text-slate-600">Sync events and create real Google Meet links</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={connectGoogleCalendar}
                      disabled={googleCalendarLoading}
                      className="self-start rounded-full bg-[#102a27] text-white hover:bg-[#173d38] sm:self-auto"
                    >
                      {googleCalendarLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Connect"
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-green-800 text-sm">Google Calendar Connected</p>
                        <p className="text-xs text-green-600">
                          {googleCalendarStatus.google_email || "Events will sync to your calendar"}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmationDialog({ open: true, type: "disconnect", id: null, title: "Disconnect Google Calendar?", description: "New events and meetings will stop syncing to this Google account. Existing Sporthouse events will remain." })}
                      className="text-green-700 hover:text-green-800 hover:bg-green-100"
                    >
                      Disconnect
                    </Button>
                  </div>
                )}

            </div>
          </TabsContent>

          <TabsContent value="locations" className="mt-0">
            {renderLocationsTab()}
          </TabsContent>

          <TabsContent value="meetings" className="mt-6">
            <div className="space-y-6" data-testid="meetings-section">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Virtual Meetings</h2>
                  <p className="text-slate-500 text-sm">Create and manage Google Meet video conferences</p>
                </div>
                
                {googleCalendarStatus.connected && (
                  <Button 
                    onClick={() => setCreateMeetingDialogOpen(true)} 
                    className="self-start rounded-full bg-[#102a27] hover:bg-[#173d38] sm:self-auto"
                    data-testid="create-meeting-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Meeting
                  </Button>
                )}
              </div>

              {/* Connection Status */}
              {!googleCalendarStatus.connected ? (
                <Card className="rounded-3xl border-dashed border-[#dfe6df]">
                  <CardContent className="py-12 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-teal-50">
                      <Video className="h-8 w-8 text-[#087f67]" />
                    </div>
                    <h3 className="font-semibold text-slate-900 text-lg mb-2">Connect Google Calendar</h3>
                    <p className="text-slate-500 mb-6 max-w-md mx-auto">
                      Connect your Google account to create Google Meet links and sync meetings to your calendar.
                    </p>
                    <Button onClick={connectGoogleCalendar} className="rounded-full bg-[#102a27] hover:bg-[#173d38]">
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Connect Google Account
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Connected Status Card */}
                  <Card className="rounded-3xl border-emerald-200 bg-emerald-50">
                    <CardContent className="py-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <Check className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-green-900">Google Calendar Connected</p>
                            <p className="text-sm text-green-700">{googleCalendarStatus.google_email}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmationDialog({ open: true, type: "disconnect", id: null, title: "Disconnect Google Calendar?", description: "New events and meetings will stop syncing to this Google account. Existing Sporthouse events will remain." })} className="text-green-700 hover:text-green-800 hover:bg-green-100">
                          Disconnect
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Meetings List */}
                  {meetingsError ? (
                    <Card className="rounded-3xl border-rose-200 bg-rose-50" role="alert">
                      <CardContent className="py-8 text-center">
                        <h3 className="font-semibold text-rose-950">Meetings unavailable</h3>
                        <p className="mx-auto mt-2 max-w-lg text-sm text-rose-800">{meetingsError}</p>
                        <Button onClick={fetchMeetings} className="mt-4 rounded-full bg-rose-700 hover:bg-rose-800">Try again</Button>
                      </CardContent>
                    </Card>
                  ) : meetingsLoading ? (
                    <Card>
                      <CardContent className="py-8 text-center">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-slate-400" />
                        <p className="text-sm text-slate-500">Loading meetings...</p>
                      </CardContent>
                    </Card>
                  ) : meetings.length === 0 ? (
                    <Card className="border-dashed">
                      <CardContent className="py-12 text-center">
                        <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="font-medium text-slate-900 mb-2">No meetings yet</h3>
                        <p className="text-slate-500 mb-4">Create your first virtual meeting with Google Meet</p>
                        <Button onClick={() => setCreateMeetingDialogOpen(true)} className="rounded-full bg-[#102a27] hover:bg-[#173d38]">
                          <Plus className="w-4 h-4 mr-2" />
                          Create Meeting
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-6">
                      {/* Upcoming Meetings */}
                      {upcomingMeetings.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 mb-3">Upcoming Meetings</h3>
                          <div className="space-y-3">
                            {upcomingMeetings.map((meeting) => (
                              <Card key={meeting.meeting_id} className="rounded-3xl border-[#dfe6df]" data-testid={`meeting-card-${meeting.meeting_id}`}>
                                <CardContent className="py-4">
                                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                    <div className="flex min-w-0 items-start gap-3 sm:gap-4">
                                      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-teal-50">
                                        <Video className="h-6 w-6 text-[#087f67]" />
                                      </div>
                                      <div>
                                        <h3 className="font-semibold text-slate-900">{meeting.title}</h3>
                                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                                          <span className="flex items-center gap-1">
                                            <CalendarIcon className="w-4 h-4" />
                                            {formatMeetingDate(meeting.start_time)}
                                          </span>
                                          <span className="flex items-center gap-1">
                                            <Clock className="w-4 h-4" />
                                            {formatMeetingTime(meeting.start_time)} - {formatMeetingTime(meeting.end_time)}
                                          </span>
                                        </div>
                                        {meeting.attendee_emails?.length > 0 && (
                                          <div className="flex items-center gap-1 mt-2 text-sm text-slate-500">
                                            <Users className="w-4 h-4" />
                                            {meeting.attendee_emails.length} attendee{meeting.attendee_emails.length !== 1 ? "s" : ""}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                                      <a
                                        href={meeting.meet_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 rounded-full bg-[#102a27] px-4 py-2 text-sm font-medium text-white hover:bg-[#173d38]"
                                        data-testid={`join-meeting-${meeting.meeting_id}`}
                                      >
                                        Join <ExternalLink className="w-4 h-4" />
                                      </a>
                                      <Button variant="ghost" size="icon" onClick={() => copyMeetLink(meeting)} aria-label={`Copy link for ${meeting.title}`}>
                                        {copiedMeetingId === meeting.meeting_id ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                                      </Button>
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        onClick={() => setDeleteMeetingDialog({ open: true, meeting })} 
                                        className="text-slate-500 hover:text-red-600"
                                        aria-label={`Delete ${meeting.title}`}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Past Meetings */}
                      {pastMeetings.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-slate-500 mb-3">Past Meetings</h3>
                          <div className="space-y-3 opacity-75">
                            {pastMeetings.slice(0, 5).map((meeting) => (
                              <Card key={meeting.meeting_id} className="bg-slate-50">
                                <CardContent className="py-4">
                                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                    <div className="flex items-start gap-4">
                                      <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-slate-200">
                                        <Video className="w-6 h-6 text-slate-500" />
                                      </div>
                                      <div>
                                        <h3 className="font-semibold text-slate-900">{meeting.title}</h3>
                                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                                          <span className="flex items-center gap-1">
                                            <CalendarIcon className="w-4 h-4" />
                                            {formatMeetingDate(meeting.start_time)}
                                          </span>
                                          <span className="flex items-center gap-1">
                                            <Clock className="w-4 h-4" />
                                            {formatMeetingTime(meeting.start_time)} - {formatMeetingTime(meeting.end_time)}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-2">
                                      <Button variant="ghost" size="icon" onClick={() => copyMeetLink(meeting)} aria-label={`Copy link for ${meeting.title}`}>
                                        {copiedMeetingId === meeting.meeting_id ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                                      </Button>
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        onClick={() => setDeleteMeetingDialog({ open: true, meeting })} 
                                        className="text-slate-500 hover:text-red-600"
                                        aria-label={`Delete ${meeting.title}`}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        <SchedulingEventDrawer
          event={selectedEvent}
          open={Boolean(selectedEvent)}
          onOpenChange={(open) => {
            if (!open) setSelectedEvent(null);
          }}
          onEdit={(event) => {
            setSelectedEvent(null);
            openEditEvent(event);
          }}
          program={programs.find((program) => program.program_id === selectedEvent?.program_id)}
          team={teams.find((team) => team.team_id === selectedEvent?.team_id)}
          conflicts={selectedEventConflicts}
        />

        <SchedulingEventDialog
          open={eventDialogOpen}
          onOpenChange={setEventDialogOpen}
          eventForm={eventForm}
          setEventForm={setEventForm}
          editingEvent={editingEvent}
          saving={saving}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
          programs={programs}
          teams={teams}
          locationOptions={locationOptions}
          googleCalendarConnected={googleCalendarStatus.connected}
        />

        <LocationDialog
          open={locationDialog}
          onOpenChange={setLocationDialog}
          locationForm={locationForm}
          setLocationForm={setLocationForm}
          editingLocation={editingLocation}
          saving={savingLocation}
          onSave={handleSaveLocation}
        />

        {/* Create Meeting Dialog */}
        <CreateMeetingDialog
          open={createMeetingDialogOpen}
          onOpenChange={setCreateMeetingDialogOpen}
          token={token}
          onMeetingCreated={() => {
            fetchMeetings();
          }}
        />

        {/* Delete Meeting Confirmation */}
        <AlertDialog open={deleteMeetingDialog.open} onOpenChange={(open) => setDeleteMeetingDialog({ ...deleteMeetingDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Meeting?</AlertDialogTitle>
              <AlertDialogDescription>
                This will delete the meeting "{deleteMeetingDialog.meeting?.title}" and remove it from Google Calendar. 
                Attendees will be notified of the cancellation.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteMeeting} className="bg-red-600 hover:bg-red-700">
                {deletingMeeting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={confirmationDialog.open} onOpenChange={(open) => setConfirmationDialog((current) => ({ ...current, open }))}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmationDialog.title}</AlertDialogTitle>
              <AlertDialogDescription>{confirmationDialog.description}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmAction} disabled={confirmingAction} className="bg-red-700 hover:bg-red-800">
                {confirmingAction ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {confirmationDialog.type === "disconnect" ? "Disconnect" : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </PageLayout>
  );
};

export default AdminScheduling;
